import { useEffect, useMemo, useState } from 'react';
import type { ShowcaseProject } from '@data/showcaseProjects';

type BeforeAfterCarouselProps = {
	projects: ShowcaseProject[];
	beforeLabel?: string;
	afterLabel?: string;
};

export default function BeforeAfterCarousel({
	projects,
	beforeLabel = 'Before',
	afterLabel = 'After'
}: BeforeAfterCarouselProps) {
	const [transformIndex, setTransformIndex] = useState(0);
	const [sliderValues, setSliderValues] = useState<Record<string, number>>({});
	const [hasTriedSlider, setHasTriedSlider] = useState(false);

	const slides = useMemo(() => projects.filter(Boolean), [projects]);
	const current = slides[transformIndex] ?? slides[0];
	const sliderPct = current ? sliderValues[current.id] ?? 50 : 50;

	useEffect(() => {
		const initial: Record<string, number> = {};
		slides.forEach((p) => {
			initial[p.id] = 50;
		});
		setSliderValues((prev) => ({ ...initial, ...prev }));
	}, [slides]);

	useEffect(() => {
		if (transformIndex >= slides.length) {
			setTransformIndex(0);
		}
	}, [slides.length, transformIndex]);

	if (!current) {
		return null;
	}

	const goPrev = () => setTransformIndex((i) => (i - 1 + slides.length) % slides.length);
	const goNext = () => setTransformIndex((i) => (i + 1) % slides.length);

	return (
		<div className="seeker-transform-carousel mx-auto">
			<div className="seeker-transform-card rounded-4 overflow-hidden">
				<div className="seeker-before-after-wrap seeker-before-after-wrap--hero">
					<img
						src={current.before}
						alt={`${current.title} before`}
						className="w-100 seeker-transform-image seeker-transform-image--hero"
					/>
					<div className="seeker-after-layer" style={{ clipPath: `inset(0 0 0 ${sliderPct}%)` }}>
						<img
							src={current.after}
							alt={`${current.title} after`}
							className="w-100 seeker-transform-image seeker-transform-image--hero"
						/>
					</div>
					<div className="seeker-slider-line" style={{ left: `${sliderPct}%` }}>
						<span className={`seeker-slider-handle ${!hasTriedSlider ? 'seeker-slider-handle--hint' : ''}`}>
							<i className="bi bi-arrow-left-right" aria-hidden />
						</span>
					</div>
					<span className="seeker-image-label before">{beforeLabel}</span>
					<span className="seeker-image-label after">{afterLabel}</span>
					<input
						type="range"
						min={0}
						max={100}
						value={sliderPct}
						onPointerDown={() => setHasTriedSlider(true)}
						onChange={(e) => {
							setHasTriedSlider(true);
							setSliderValues((prev) => ({
								...prev,
								[current.id]: Number(e.target.value)
							}));
						}}
						className="seeker-slider-input"
						aria-label={`Before and after slider for ${current.title}`}
					/>
				</div>
				<div className="p-3 p-md-4 bg-white border border-top-0">
					<div className="d-flex flex-column flex-md-row align-items-start align-items-md-end justify-content-between gap-3">
						<div className="flex-grow-1 pe-md-3">
							<div className="badge rounded-pill bg-success-subtle text-success-emphasis border border-success-subtle mb-2">
								{current.style}
							</div>
							<h3 className="h4 text-success-emphasis mb-1">{current.title}</h3>
							<p className="text-muted small mb-0">{current.description}</p>
						</div>
						{slides.length > 1 && (
							<div className="d-flex align-items-center gap-2 align-self-end ms-md-auto">
								<button
									type="button"
									className="btn btn-outline-success rounded-circle seeker-carousel-arrow"
									onClick={goPrev}
									aria-label="Previous transformation"
								>
									<i className="bi bi-chevron-left" aria-hidden />
								</button>
								<button
									type="button"
									className="btn btn-outline-success rounded-circle seeker-carousel-arrow"
									onClick={goNext}
									aria-label="Next transformation"
								>
									<i className="bi bi-chevron-right" aria-hidden />
								</button>
							</div>
						)}
					</div>
					{slides.length > 1 && (
						<div className="d-flex justify-content-center gap-2 mt-3 pt-2 border-top border-light-subtle">
							{slides.map((p, i) => (
								<button
									key={p.id}
									type="button"
									className={`seeker-carousel-dot ${i === transformIndex ? 'is-active' : ''}`}
									onClick={() => setTransformIndex(i)}
									aria-label={`Show ${p.title}`}
									aria-current={i === transformIndex ? 'true' : undefined}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
